import {AxiosInstance, AxiosResponse} from "axios";
import {AxiosUtil} from "@app/utils/AxiosUtil";
import {SentryHelper} from "@app/helpers/SentryHelper";

export interface SessionInfo {
    id: number;
    fingerprintHash: string;
    userAgent: string | null;
    ipAddress: string | null;
    createdAt: string;
    expiresAt: string;
    isCurrent?: boolean;
}

export class SessionService {

    private static axiosInstance: AxiosInstance;

    private static getAxiosInstance(): AxiosInstance {
        if (!this.axiosInstance) {
            this.axiosInstance = AxiosUtil.createPreconfiguredAxiosInstance();
        }

        return this.axiosInstance;
    }

    /**
     * Get all active sessions for the authenticated user
     */
    static async getActiveSessions(refreshToken?: string): Promise<SessionInfo[]> {
        return this.getAxiosInstance().get(
            '/v1/auth/sessions',
            refreshToken ? { headers: { 'x-refresh-token': refreshToken } } : undefined,
        ).then(
            (response: AxiosResponse) => {
                return Promise.resolve(<SessionInfo[]>response.data ?? [])
            },
            (error) => {
                console.log('Get active sessions failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error)
            }
        )
    };

    /**
     * Revoke (delete) a specific session
     */
    static async revokeSession(sessionId: number): Promise<void> {
        return this.getAxiosInstance().delete(
            `/v1/auth/sessions/${sessionId}`,
        ).then(
            () => {
                return Promise.resolve()
            },
            (error) => {
                console.log('Revoke session failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error)
            }
        )
    };

    /**
     * Revoke all sessions for the authenticated user
     */
    static async revokeAllSessions(): Promise<void> {
        return this.getAxiosInstance().post(
            '/v1/auth/sessions/revoke-all',
        ).then(
            () => {
                return Promise.resolve()
            },
            (error) => {
                console.log('Revoke all sessions failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error)
            }
        )
    };

    /**
     * Revoke all sessions except the current one
     */
    static async revokeOtherSessions(refreshToken: string): Promise<void> {
        return this.getAxiosInstance().post(
            '/v1/auth/sessions/revoke-others',
            { refreshToken },
        ).then(
            () => {
                return Promise.resolve()
            },
            (error) => {
                console.log('Revoke other sessions failed', error);
                SentryHelper.apiCatch(error);
                return Promise.reject(error?.response?.data ?? error)
            }
        )
    };
}
